<template>
  <main role="main" class="py-4">
    <div class="mx-5 mb-3">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("systemLogs") }}</h4>
        <div>
          <b-button variant="outline-secondary" class="mr-4" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button variant="outline-secondary" v-b-modal:log-policy-settings-modal>
            <b-icon-gear></b-icon-gear> {{ $t("logPolicy") }}
          </b-button>
        </div>
      </div>

      <b-form class="d-flex flex-row align-items-center mb-3" @submit.prevent="onFilterFormSubmit">
        <div class="flex-shrink-0 mr-3">Level:</div>
        <b-form-select class="mr-5" :options="logLevelOptions" v-model="logLevel"/>
        <div class="flex-shrink-0 mr-3">{{ $t("search") }}:</div>
        <b-form-input type="search" v-model="searchText" minlength="2" maxlength="50" class="mr-5" />
        <b-button type="submit" class="flex-shrink-0" variant="primary">{{ $t("search") }}</b-button>
      </b-form>

      <b-table ref="table" :items="tableItemProvider" hover small primary-key="id" :fields="tableFields" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button variant="danger" @click="onClickDelete(data.item)">{{ $t("actionDelete") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>

      <div class="d-flex flex-row align-items-center justify-content-between">
        <div></div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>
    </div>

    <b-modal id="log-policy-settings-modal" :title="$t('logPolicy')" hide-footer no-close-on-backdrop>
      <LogPolicySettingsModal modal-id="log-policy-settings-modal" />
    </b-modal>
  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import { DateTime } from "luxon";
  import BaseComponent from "@/components/BaseComponent";
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import {BvTableFieldArray} from "bootstrap-vue";
  import * as systemLogsService from "@/service/systemLogsService";
  import LogPolicySettingsModal from "@/components/system-logs/LogPolicySettingsModal.vue";

  @Component({
    components: { LogPolicySettingsModal }
  })
  export default class SystemLogs extends BaseComponent {

    readonly logLevelOptions = [
      { value: null, text: this.$t("all").toString() },
      { value: "INFO", text: "INFO" },
      { value: "WARN", text: "WARN" },
      { value: "ERROR", text: "ERROR" },
      { value: "FATAL", text: "FATAL" },
    ];
    logLevel = null;
    searchText = "";

    @Ref("table") table: BTable;
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#",
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
      },
      {
        key: "logDate",
        label: this.$t("datetime").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
        formatter: it => DateTime.fromISO(it).toFormat("dd/MM/yyyy HH:mm:ss")
      },
      {
        key: "thread",
        label: "Thread",
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
      },
      {
        key: "logLevel",
        label: "Level",
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
      },
      {
        key: "logger",
        label: "Logger",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      // {
      //   key: "method",
      //   label: "Method",
      //   thClass: "text-nowrap",
      //   tdClass: "text-nowrap",
      // },
      // {
      //   key: "line",
      //   label: "Line",
      //   thClass: "text-nowrap",
      //   tdClass: "text-nowrap",
      // },
      {
        key: "message",
        label: "Message",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "exception",
        label: "Exception",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      }
    ];

    itemsPerPageSelections = [
      { value: 10, text: "10 " + this.$t("itemsPerPage").toString() },
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 100;
    currentPage = 1;
    totalItems = 0;

    mounted() {

    }

    onClickReloadBtn() {
      this.logLevel = null;
      this.searchText = "";
      this.currentPage = 1;
      this.table.refresh();
    }

    onFilterFormSubmit() {
      this.table.refresh();
    }

    refreshData() {
      this.table.refresh();
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      const res = await systemLogsService.getAll({
        page: ctx.currentPage,
        pageSize: this.itemsPerPage,
        q: this.searchText?.trim(),
        logLevel: this.logLevel
      });

      this.totalItems = res.total;
      return res.data;
    }

    async onClickDelete(item) {
      const modalMessage = this.$t("deleteLogConfirm", { id: item.id }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
        centered: true
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await systemLogsService.del(item.id);
          this.table.refresh();
          this.toastSuccessDelay(this.$t("deleteLogSuccessful").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

  }
</script>
